/**
 * Thumbnails, made in the browser and pushed straight to S3.
 *
 * Doing it here rather than server-side is what keeps the API off the media
 * path entirely: the file never passes through Lambda, so neither should its
 * poster frame. The browser already has to decode the file to check it, so the
 * marginal cost of one more decode is small, and the result is a WebP a few
 * kilobytes across that the library grid can load a hundred of.
 *
 * A thumbnail is a convenience, never a requirement. `thumbKey` is nullable on
 * the stored asset for exactly this reason: if the canvas refuses to encode WebP
 * or a video will not seek, the upload carries on without one and the library
 * shows a placeholder.
 */

import { THUMBNAIL_MAX_EDGE } from '@pbtv/shared';
import type { MediaKind } from './probe.js';

export const THUMBNAIL_TYPE = 'image/webp';
const THUMBNAIL_QUALITY = 0.82;

/** How long to wait for a video element to produce a decodable frame. */
const VIDEO_FRAME_TIMEOUT_MS = 15_000;

export interface ThumbnailSize {
  width: number;
  height: number;
}

/**
 * The box a source of these dimensions fits into, longest edge capped.
 *
 * Deliberately never upscales: a 200 × 120 logo stays 200 × 120 rather than
 * being blown up to a blurry 480 wide.
 */
export function thumbnailBox(
  width: number,
  height: number,
  maxEdge: number = THUMBNAIL_MAX_EDGE,
): ThumbnailSize {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return { width: 1, height: 1 };
  }

  const longEdge = Math.max(width, height);
  if (longEdge <= maxEdge) {
    return { width: Math.round(width), height: Math.round(height) };
  }

  const scale = maxEdge / longEdge;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

async function encode(source: CanvasImageSource, size: ThumbnailSize): Promise<Blob | null> {
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;

  const context = canvas.getContext('2d');
  if (context === null) return null;
  context.drawImage(source, 0, 0, size.width, size.height);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, THUMBNAIL_TYPE, THUMBNAIL_QUALITY);
  });

  // A browser that cannot encode WebP silently hands back a PNG instead, and the
  // presigned PUT is signed for WebP. Rather than upload something the signature
  // rejects, go without: the asset simply has no thumbnail.
  if (blob === null || blob.type !== THUMBNAIL_TYPE) return null;
  return blob;
}

async function imageThumbnail(file: Blob): Promise<Blob | null> {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file);
    try {
      return await encode(bitmap, thumbnailBox(bitmap.width, bitmap.height));
    } finally {
      bitmap.close();
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('The browser could not decode this image.'));
      element.src = url;
    });
    return await encode(image, thumbnailBox(image.naturalWidth, image.naturalHeight));
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Grabs the opening frame of a clip.
 *
 * The seek target is a fraction of a second in rather than exactly zero. Asking
 * for 0 is legal but several decoders answer it before any frame has been
 * composited, and the canvas comes back blank; nudging past the first keyframe
 * costs nothing and always produces a picture.
 */
async function videoThumbnail(file: Blob): Promise<Blob | null> {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.preload = 'metadata';
  video.muted = true;
  video.playsInline = true;

  try {
    const frame = await new Promise<ThumbnailSize | null>((resolve) => {
      const timer = window.setTimeout(() => finish(null), VIDEO_FRAME_TIMEOUT_MS);
      let settled = false;

      function finish(size: ThumbnailSize | null): void {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve(size);
      }

      video.onerror = () => finish(null);
      video.onseeked = () => finish({ width: video.videoWidth, height: video.videoHeight });
      video.onloadedmetadata = () => {
        video.currentTime = Math.min(0.1, (video.duration || 1) / 4);
      };

      video.src = url;
    });

    if (frame === null || frame.width === 0 || frame.height === 0) return null;
    return await encode(video, thumbnailBox(frame.width, frame.height));
  } finally {
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(url);
  }
}

/** Never throws: a thumbnail that cannot be made is `null`, not a failed upload. */
export async function makeThumbnail(file: Blob, kind: MediaKind): Promise<Blob | null> {
  try {
    return kind === 'video' ? await videoThumbnail(file) : await imageThumbnail(file);
  } catch {
    return null;
  }
}

/**
 * Sends the thumbnail to the presigned URL the initiate response handed over.
 *
 * The content type is stated explicitly because that is what the server signed
 * the URL for; omitting it on a signature that covers it is a 403.
 */
export async function putThumbnail(url: string, blob: Blob, signal?: AbortSignal): Promise<boolean> {
  try {
    const response = await fetch(url, {
      method: 'PUT',
      body: blob,
      headers: { 'Content-Type': THUMBNAIL_TYPE },
      signal,
    });
    return response.ok;
  } catch {
    return false;
  }
}
